import {attempt} from 'es-toolkit';
import {isNull, isObject} from 'es-toolkit/compat';
import {useRouter} from 'vue-router';
import logToMonitor from '@/utils/logging';

export const RETURN_ROUTE_KEY = 'returnRoute';

export type StoredReturnRoute = {
  name: string;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
};

/**
 * Reads and clears the saved return route. Standalone (no router dependency) so the navigation
 * guard can import it without a circular dependency with `@/router`.
 *
 * Clears the key up front so a corrupt or stale payload can never re-trigger a redirect, mirroring
 * the `afterPayment` resume handling in `useShipmentLabel`.
 */
export const consumeReturnRoute = (): StoredReturnRoute | null => {
  const raw = localStorage.getItem(RETURN_ROUTE_KEY);
  localStorage.removeItem(RETURN_ROUTE_KEY);

  if (!raw) {
    return null;
  }

  const [parseError, parsed] = attempt<unknown, Error>(() => JSON.parse(raw));

  if (
    parseError ||
    isObject(parsed) === false ||
    isNull(parsed) === true ||
    !('name' in parsed) ||
    typeof parsed.name !== 'string'
  ) {
    logToMonitor(parseError ?? new Error('Invalid returnRoute shape'), {
      _myparcel_extra_info: 'returnRoute localStorage payload could not be parsed or had an invalid shape',
    });
    return null;
  }

  return parsed as StoredReturnRoute;
};

export const useReturnRoute = () => {
  const router = useRouter();

  /**
   * Saves a named route to return to after an external redirect (e.g. a payment provider).
   * Pass an explicit route, or omit it to capture the current route. Unnamed routes are skipped;
   * the guard then falls back to dashboard.
   */
  const setReturnRoute = (route?: StoredReturnRoute): void => {
    if (route) {
      localStorage.setItem(RETURN_ROUTE_KEY, JSON.stringify(route));
      return;
    }

    const {name, params, query} = router.currentRoute.value;

    if (typeof name !== 'string') {
      return;
    }

    localStorage.setItem(RETURN_ROUTE_KEY, JSON.stringify({name, params, query} satisfies StoredReturnRoute));
  };

  return {setReturnRoute};
};
